import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

declare global {
  interface Window {
    dataLayer: any[] | undefined;
    Osano?: any;
  }
}

type AppLanguage = 'en' | 'fr';

@Injectable({ providedIn: 'root' })
export class OsanoLoaderService {
  private loaded = false;

  constructor() {}

  load(languageCode: AppLanguage = 'en'): Promise<void> {
    if (this.loaded) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      try {
        const osanoLocale = OsanoLoaderService.mapToOsanoLocale(languageCode);
        window.dataLayer = window.dataLayer || [];

        this.pushLanguageToDataLayer(osanoLocale);

        window.dataLayer.push({
          'gtm.start': Date.now(),
          event: environment.gtmFileName,
        });

        const gtmScript = document.createElement('script');
        gtmScript.async = true;
        gtmScript.src = `${environment.gtmURL}${environment.gtmId}`;

        gtmScript.onload = () => {
          this.loaded = true;
          this.updateOsanoLocale(languageCode);
          resolve();
        };

        gtmScript.onerror = (error) => {
          console.error('Error loading GTM script:', error);
          reject(error);
        };

        const target = document.head || document.body;
        if (!target) throw new Error('Cannot load GTM: no <head> or <body> element');

        target.appendChild(gtmScript);

        this.addGtmIframeFallback();
      } catch (error) {
        console.error('Error initializing GTM/Osano:', error);
        reject(error);
      }
    });
  }

  updateOsanoLocale(appLang: AppLanguage): void {
    const osanoLocale = OsanoLoaderService.mapToOsanoLocale(appLang);

    this.pushLanguageToDataLayer(osanoLocale);

    try {
      if (window.Osano?.cm) {
        window.Osano.cm.locale = osanoLocale;
        window.Osano.cm.render?.();
      } else if (typeof window.Osano === 'function') {
        window.Osano('locale', osanoLocale);
        window.Osano('onInitialized', () => window.Osano?.cm?.render?.());
      }
    } catch (e) {
      console.warn('Failed to switch Osano locale', e);
    }
  }

  static mapToOsanoLocale(languageCode: string): string {
    switch (languageCode.toLowerCase()) {
      case 'fr':
      case 'fr-ca':
        return 'fr-ca';
      default:
        return 'en';
    }
  }

  private pushLanguageToDataLayer(osanoLocale: string): void {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'osanoLanguageUpdate',
      osano_language: osanoLocale,
    });
  }

  private addGtmIframeFallback(): void {
    const iframe = document.createElement('iframe');
    iframe.src = `${environment.gtmURLns}${environment.gtmId}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';

    if (document.body) {
      document.body.insertBefore(iframe, document.body.firstChild);
    }
  }
}
